import React, { useCallback, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@this/src/components/shared/ui/feedbacks/modal';
import { styled } from '@this/src/components/constants/themes';
import type Hotel from '@this/src/domain/hotel/hotel';
import type ReserveInfo from '@this/src/domain/reserve_info';
import { ButtonBase } from '@this/shared/atoms/button';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { StyledReserveErrorAlert, StyledAlertTitle } from '@this/components/shared/reserve_ui_components';
import { Section } from '@this/components/reserve_trip/reserve_confirm/routings/input_customer_information/trip_rule_section/trip_rule_error_section';
import type { TripDuplicationInterface } from '../../reserve_confirm';
import TripDuplicationSection from '../input_customer_information/trip_duplication_section/trip_duplication_section';
import RestrictedHotelSection from '../input_customer_information/restricted_hotel_section/restricted_hotel_section';

export interface PassportWarningTraveler {
  userId: number | null;
  name: string;
  passportExpire: string;
  remainingMonths: number;
}

interface Props {
  open: boolean;
  tripDuplications: TripDuplicationInterface[];
  restrictedHotels: Hotel[];
  passportWarningTravelers: PassportWarningTraveler[];
  reserveInfo: ReserveInfo;
  onClose: () => void;
  onSubmit: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

export const ConfirmCustomerConfirmModal: React.FC<Props> = ({
  open,
  tripDuplications,
  restrictedHotels,
  passportWarningTravelers,
  reserveInfo,
  onClose,
  onSubmit
}) => {
  const [tripDuplicationAgree, setTripDuplicationAgree] = useState(false);
  const [restrictedHotelAgree, setRestrictedHotelAgree] = useState(false);
  const [passportWarningAgree, setPassportWarningAgree] = useState(false);

  const groupedRestrictedHotels = useMemo(() => {
    return Object.values(
      restrictedHotels.reduce((acc, hotel) => {
        const key = hotel.name ?? '';
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(hotel);
        return acc;
      }, {} as Record<string, Hotel[]>)
    );
  }, [restrictedHotels]);

  const tripDuplicationConfirm = useMemo(() => {
    return tripDuplications.length > 0;
  }, [tripDuplications]);

  const restrictedHotelConfirm = useMemo(() => {
    return restrictedHotels.length > 0;
  }, [restrictedHotels]);

  const passportWarningConfirm = useMemo(() => {
    return passportWarningTravelers.length > 0;
  }, [passportWarningTravelers]);

  const handleTripDuplicationAgree = useCallback(() => {
    setTripDuplicationAgree(agree => !agree);
  }, []);

  const handleRestrictedHotelAgree = useCallback(() => {
    const agree = !restrictedHotelAgree;
    reserveInfo.setUserChecker(agree);
    setRestrictedHotelAgree(agree);
  }, [restrictedHotelAgree]);

  const handlePassportWarningAgree = useCallback(() => {
    setPassportWarningAgree(agree => !agree);
  }, []);

  const agree = useMemo(() => {
    if (tripDuplicationConfirm && !tripDuplicationAgree) {
      return false;
    }

    if (restrictedHotelConfirm && !restrictedHotelAgree) {
      return false;
    }

    if (passportWarningConfirm && !passportWarningAgree) {
      return false;
    }

    return true;
  }, [
    tripDuplicationAgree,
    restrictedHotelAgree,
    passportWarningAgree,
    tripDuplicationConfirm,
    restrictedHotelConfirm,
    passportWarningConfirm
  ]);

  const handleClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (passportWarningConfirm && passportWarningAgree) {
        reserveInfo.setPassportWarningTravelers(
          passportWarningTravelers.map(t => ({
            userId: t.userId,
            passportExpire: t.passportExpire
          }))
        );
      }
      onSubmit(e);
      onClose();
    },
    [onClose, onSubmit, passportWarningConfirm, passportWarningAgree, passportWarningTravelers, reserveInfo]
  );

  return (
    <Modal open={open} onClose={onClose} data-testid="reserve-confirm-before-submit-modal-root">
      <ModalHeader className="reserve-confirm-before-submit-modal-header">
        <HeaderContent data-testid="reserve-confirm-before-submit-modal-title">依頼前に確認ください</HeaderContent>
      </ModalHeader>
      <ModalBody>
        <Content>
          <p>以下の旅程を確認し、問題なければ手配を依頼してください。</p>
          <ConfirmContents>
            {tripDuplicationConfirm && (
              <ConfirmContent data-testid="reserve-confirm-before-submit-modal-content-trip-duplication">
                <label>
                  <input
                    type="checkbox"
                    className="trip_duplication_agree"
                    data-testid="reserve-confirm-before-submit-modal-check-trip-duplication"
                    checked={tripDuplicationAgree}
                    onChange={handleTripDuplicationAgree}
                  />
                  旅程の重複を確認しました
                </label>
                <div>
                  {tripDuplications.map((tripDuplication, index) => (
                    <TripDuplicationSection key={index} tripDuplication={tripDuplication} />
                  ))}
                </div>
              </ConfirmContent>
            )}
            {restrictedHotelConfirm && (
              <ConfirmContent data-testid="reserve-confirm-before-submit-modal-content-restricted-hotel">
                <label>
                  <input
                    type="checkbox"
                    className="restricted_hotel_agree"
                    data-testid="reserve-confirm-before-submit-modal-check-restricted-hotel"
                    checked={restrictedHotelAgree}
                    onChange={handleRestrictedHotelAgree}
                  />
                  宿泊時の条件を確認しました
                </label>
                <div>
                  {groupedRestrictedHotels.map((hotelGroup, index) => (
                    <RestrictedHotelSection key={index} restrictedHotels={hotelGroup} />
                  ))}
                </div>
              </ConfirmContent>
            )}
            {passportWarningConfirm && (
              <ConfirmContent data-testid="reserve-confirm-before-submit-modal-content-passport-warning">
                <label>
                  <input
                    type="checkbox"
                    className="passport_warning_agree"
                    data-testid="reserve-confirm-before-submit-modal-check-passport-warning"
                    checked={passportWarningAgree}
                    onChange={handlePassportWarningAgree}
                  />
                  パスポートの残存期間について確認しました
                </label>
                {passportWarningTravelers.map((traveler, index) => (
                  <StyledReserveErrorAlert
                    key={index}
                    data-testid={`reserve-confirm-before-submit-modal-passport-warning-item-${index}`}
                    icon={<ErrorOutlineIcon />}
                    variant="filled"
                    severity="error"
                  >
                    <StyledAlertTitle>
                      {traveler.name}様が入力されたパスポート情報では、残存期間が{traveler.remainingMonths}
                      ヶ月となっています
                    </StyledAlertTitle>
                    <Section>
                      <p>・渡航先の国によっては入国時に6ヶ月以上のパスポート残存期間が必要です。</p>
                      <p>・残存期間が不足している場合、出国できない可能性があります。</p>
                      <p>・内容を変更される場合、アカウント設定から変更してください。</p>
                      ※残存期間の不足により出張が不可能となった場合の費用（キャンセル料等）は、お客様のご負担となります
                    </Section>
                  </StyledReserveErrorAlert>
                ))}
              </ConfirmContent>
            )}
          </ConfirmContents>
        </Content>
      </ModalBody>
      <ModalFooter>
        <ConfirmButton
          type="button"
          role="confirm"
          onClick={handleClick}
          disabled={!agree}
          data-testid="reserve-confirm-before-submit-modal-submit"
        >
          手配を依頼する
        </ConfirmButton>
      </ModalFooter>
    </Modal>
  );
};

const HeaderContent = styled.div`
  padding: 5px 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 0;
`;

const ConfirmButton = styled.button`
  ${ButtonBase}
  margin-left: 15px;
  padding: 0.75em 1em;
  line-height: 1;
`;

const ConfirmContents = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ConfirmContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
